/**
 * useLiveRefresh — app-wide "no reload needed" data freshness.
 *
 * The app fetches with manual `useEffect` + Supabase calls (not React Query), so
 * there's no global refetch switch. This provides one cheaply:
 *
 *   • A global `tick` counter in a tiny Zustand store.
 *   • `useLiveRefreshManager()` (mounted ONCE in AppLayout) bumps the tick when
 *       – the browser tab regains focus / becomes visible, and
 *       – any of the org's core tables change (Supabase Realtime).
 *   • Screens call `useLiveTick()` and add it to their fetch-effect deps, so they
 *     re-run their existing loader on any live change — no rewrite of the fetch.
 *   • Screens gate their skeleton with `useFirstLoad()` so tick-driven refetches
 *     are SILENT: current data stays on screen and updates in place. Only the
 *     first load (or an org/filter change) shows the skeleton.
 *
 * Realtime is best-effort: a table only pushes if it's in the `supabase_realtime`
 * publication (see migration 00240). The focus/visibility refresh works regardless,
 * so returning to the tab always shows fresh data even for tables not yet published.
 *
 * @since 3.0.0
 */
import { create } from 'zustand';
import { useCallback, useEffect, useRef } from 'react';
import { supabase } from '@/db/supabase';
import { useLiveChannel } from '@/shared/utils/liveChannel';
import { useAuth } from './useAuth';
import type { Org } from '@/types/db';

interface LiveState {
    tick: number;
    bump: () => void;
}

export const useLiveStore = create<LiveState>((set) => ({
    tick: 0,
    bump: () => set((s) => ({ tick: s.tick + 1 })),
}));

/** Read the live tick; add it to a fetch effect's deps to auto-refresh on change. */
export function useLiveTick(): number {
    return useLiveStore((s) => s.tick);
}

/**
 * Silent live refresh. Call the returned function at the START of a fetch:
 * it's true only for the first fetch of `key` (mount, org switch, filter
 * change) — show the loading skeleton then. Every later run with the same key
 * (live-tick, tab focus, post-save refetch) returns false: keep the current
 * data on screen and swap it in place when the fetch lands, instead of
 * blanking the whole screen back to a skeleton on every background refresh.
 *
 * `key` = everything the fetch depends on EXCEPT the live tick (org id, date
 * range, filters…) so a real input change still shows the skeleton.
 */
export function useFirstLoad(key: string | null | undefined): () => boolean {
    const loaded = useRef<string | null>(null);
    return useCallback(() => {
        const k = key ?? '';
        if (loaded.current === k) return false;
        loaded.current = k;
        return true;
    }, [key]);
}

// The org-scoped tables whose changes should refresh open screens. All carry an
// `org_id`, so the realtime subscription is tenant-filtered.
//
// Every name here must be a table that actually EXISTS. `expenses` sat in this
// list and in no schema anywhere — an expense is a `transactions` row, so the
// subscription could never fire and only made the publication audit look
// incomplete (2026-07-27: `realtime_published_tables()` on the live database
// listed all 16 real tables and, correctly, no `expenses`).
export const CORE_TABLES = [
    'transactions',
    'journal_entries',
    'contacts',
    'items',
    'accounts',
    'bank_feeds',
    'branches',
    'org_members',
    'invitations',
    // em-20: an offer is accepted in the CANDIDATE's browser, so without these
    // the employer's roster has no way to learn it happened.
    'employees',
    'employee_letters',
    // addedonce ao-7: the rest of the register's tables (src/shared/entities),
    // published by 20261023021657_addedonce_publish_entity_tables_to_realtime.
    // Until that paste they refresh on tab focus, as before.
    'bank_accounts',
    'org_registrations',
    'warehouses',
    'cost_centers',
    'departments',
    'designations',
    'item_units',
    // stay-9: a room cleaned on a housekeeper's phone shows at every front desk
    // at once. Published by 20261029060714_stay9_...; until that paste it
    // refreshes on tab focus, as before.
    'stay_rooms',
];

/**
 * Mount ONCE (AppLayout). Wires tab-focus/visibility + org realtime to the global
 * tick. Debounced so a burst of changes (e.g. a multi-line post) triggers one refresh.
 */
export function useLiveRefreshManager(): void {
    const { currentOrg } = useAuth();
    const orgId = currentOrg?.id;
    const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const lastBump = useRef(0);
    // Bridge from the focus/visibility effect to the realtime hook below, so the
    // two share one debounce/throttle instead of racing each other.
    const bumpRef = useRef<(() => void) | null>(null);
    const debugRef = useRef(false);
    // When org_config was last re-read on a tab return (0: never, so the first return always reads).
    const lastOrgRead = useRef(0);

    useEffect(() => {
        const bump = useLiveStore.getState().bump;
        const fireBump = () => {
            timer.current = null;
            lastBump.current = Date.now();
            bump();
        };
        // Focus/visibility: refresh promptly (400ms coalesces the focus +
        // visibilitychange pair that fires together on tab return).
        const debouncedBump = () => {
            if (timer.current) clearTimeout(timer.current);
            timer.current = setTimeout(fireBump, 400);
        };
        // Realtime: same 400ms coalescing, but at most one bump per 15s. A hot
        // table (bulk import, another device posting a batch, a runaway writer)
        // must not strobe every open screen with a refetch per change.
        const REALTIME_MIN_GAP = 15_000;
        const throttledBump = () => {
            if (timer.current) return; // a bump is already scheduled
            const wait = Math.max(400, REALTIME_MIN_GAP - (Date.now() - lastBump.current));
            timer.current = setTimeout(fireBump, wait);
        };
        // Turn on `localStorage.karr_live_debug = '1'` to see which table is
        // generating realtime traffic (diagnoses "why does the app keep refreshing").
        let debugLive = false;
        try { debugLive = localStorage.getItem('karr_live_debug') === '1'; } catch { /* ignore */ }

        // Record "last active" for the access-governance roster — throttled to once
        // per hour per org (localStorage) so it's a cheap heartbeat, not a firehose.
        const pingActivity = () => {
            if (!orgId) return;
            try {
                const key = `karr_active_ping_${orgId}`;
                const last = Number(localStorage.getItem(key) || 0);
                if (Date.now() - last < 3_600_000) return;
                localStorage.setItem(key, String(Date.now()));
            } catch { /* storage disabled — ping anyway */ }
            void supabase.rpc('touch_my_activity', { p_org_id: orgId });
        };
        pingActivity();

        // practice-door (25 Oct 2026): the org's settings (modules, the sell
        // profile) are read once per session, so a module switched on by SQL or
        // from another device stayed hidden until someone knew to reload. On
        // the way back to the tab, at most once a minute, read org_config again
        // and apply a change in place: the sidebar and every desk follow it.
        // `orgs` is not in the realtime publication, so this is the one door.
        const refreshOrgSettings = () => {
            if (!orgId || Date.now() - lastOrgRead.current < 60_000) return;
            lastOrgRead.current = Date.now();
            void supabase.from('orgs').select('org_config').eq('id', orgId).maybeSingle().then(({ data, error }) => {
                if (error || !data) return;   // a failed read leaves the session as it was; the next return tries again
                const now = useAuth.getState().currentOrg;
                if (now?.id !== orgId) return;
                const next = (data as { org_config: Org['org_config'] }).org_config;
                if (JSON.stringify(next ?? null) !== JSON.stringify(now.org_config ?? null)) {
                    useAuth.getState().updateOrgLocal(orgId, { org_config: next });
                }
            }, () => { /* offline: try on the next return */ });
        };

        // 1. Tab focus / visibility — always refresh returning users.
        const onFocus = () => { debouncedBump(); pingActivity(); refreshOrgSettings(); };
        const onVisible = () => { if (document.visibilityState === 'visible') { debouncedBump(); pingActivity(); refreshOrgSettings(); } };
        window.addEventListener('focus', onFocus);
        document.addEventListener('visibilitychange', onVisible);

        // (2. Realtime lives in the useLiveChannel call below — it has to be a
        //  hook so the subscription can heal itself.)
        bumpRef.current = throttledBump;
        debugRef.current = debugLive;

        return () => {
            window.removeEventListener('focus', onFocus);
            document.removeEventListener('visibilitychange', onVisible);
            if (timer.current) clearTimeout(timer.current);
        };
    }, [orgId]);

    // 2. Realtime on this org's core tables — instant cross-device freshness, on a
    //    channel that re-subscribes itself instead of dying quietly on a dropped
    //    socket (which is what made "reload to see it" the norm).
    // addedonce ao-7 (docs/ADDED_ONCE_SPEC.md §3.5): a change to a register
    // table is a message, not only a tick. The pickers of that entity refetch
    // their one list within a second (coalesced per entity, entities/tell.ts),
    // so a bank account added on another device is in this one's pickers
    // without the 15 s wait. tell.ts loads on the first such change, so the
    // register stays out of the first load.
    useLiveChannel(orgId ? `live:${orgId}` : null, useCallback((channel) => {
        const told = (table: string) => {
            if (!orgId) return;   // tell.ts tells only the register's tables (entityOfTable)
            void import('@/shared/entities/tell').then((m) => m.tellTable(table, orgId)).catch(() => { /* the tick still refreshes */ });
        };
        for (const table of CORE_TABLES) {
            channel.on(
                'postgres_changes',
                { event: '*', schema: 'public', table, filter: `org_id=eq.${orgId}` },
                (payload) => {
                    if (debugRef.current) console.info('[live-refresh]', table, payload.eventType);
                    told(table);
                    bumpRef.current?.();
                },
            );
        }
    }, [orgId]), {
        // Reconnected: anything that changed while we were off the air was never
        // delivered, so refresh the open screens once.
        onResubscribe: () => { useLiveStore.getState().bump(); },
    });
}
